// V2 Compares section config — the three compare pages the "/compares" tab embeds.
//
// There is NO fourth app and NO fourth token handshake here. The compare pages live in the Player, Guild
// and GAC apps respectively (compare-system.md §14.1: "the Website section is a tabbed shell around three
// embeds, not a fourth implementation"), so this module only names their URLs; the origins, the gates and
// the parent-token minting all come from utils/v2player.ts, utils/v2guild.ts and utils/v2gac.ts unchanged.
//
// ⚠️ Which means a compare embed inherits its app's token audience — aud="player", aud="guild", aud="gac".
// Handing the Player app a GAC token would not "mostly work"; each BFF is configured with its own
// PARENT_TOKEN_AUDIENCE and rejects the others. See ComparesV2/index.tsx, where the active tab decides
// both the expected origin and which fetch*Token to call.

import { getV2PlayerUrl, isV2PlayerEnabled } from "./v2player";
import { getV2GuildUrl, isV2GuildEnabled } from "./v2guild";
import { getV2GacUrl, isV2GacEnabled } from "./v2gac";

/** The Player app's compare page (HotUtilsV2-Player #44). Its settings editor is at /compare/settings. */
export function getV2PlayerCompareUrl(): string {
  return getV2PlayerUrl() + "/compare";
}

/** The Guild app's compare page (HotUtilsV2-Guild #11). */
export function getV2GuildCompareUrl(): string {
  return getV2GuildUrl() + "/compare";
}

/**
 * The GAC app's Compare TAB — the same page its own GAC section shows, one implementation with two mounts
 * (§14.1). The GAC app selects its section from a `tab` query param rather than a path, hence `?tab=`.
 *
 * 🔴 NOT SHIPPED YET (compare-system.md P3b). Until the GAC app grows that tab it ignores the param and
 * opens its default view, so this tab shows Grand Arena rather than an error — deliberately: the wiring is
 * then already correct on the day the app ships, with nothing here to change. The same "wire it first, gate
 * it off" order the other V2 embeds used for their URLs.
 */
export function getV2GacCompareUrl(): string {
  return getV2GacUrl() + "?tab=compare";
}

/**
 * The V2-Compares section is visible where at least one of its three apps is — derived from the app gates
 * rather than repeating their host list, so it cannot drift out of sync with them. Today all three are
 * TRUE only on development.hotutils.com and localhost.
 *
 * The ROUTE and the MENU ITEM must both use this, or the tab appears where it cannot work; the individual
 * TABS are gated by their own app's flag (see ComparesV2/index.tsx), so a single app graduating to
 * production brings only its own tab with it.
 */
export function isV2ComparesEnabled(): boolean {
  return isV2PlayerEnabled() || isV2GuildEnabled() || isV2GacEnabled();
}
